import React, { useMemo } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import LoginPage from '../Pages/Login';
import Verify2FAPage from '../Pages/Login/Pages/Verify2FA';
import NotAuthenticatedPage from '../Pages/Login/Pages/NotAuthenticated';
import Setup2FA from '../Pages/Login/Pages/Setup2FA';
import SetupFIDO2 from '../Pages/Login/Pages/SetupFIDO2';
import ChangePwd from '../Pages/Login/Pages/ChangePwd';
import LandingPage from '../Public/LandingPage';
import CallToActionPage from '../Public/CallToActionPage';
import ResponseRegisteredPage from '../Public/ResponseRegistered';
import UnauthenticatedLayoutPage from './Components/UnauthenticatedLayoutPage';
import AuthenticatedLayoutPage from './Components/AuthenticatedLayoutPage';
import SettingsPage from '../Pages/SettingsPage';
import PurchaseHistoryPage from '../Pages/PurchaseHistory';
import ErrorBoundaryPage from '../Pages/ErrorBoundary/ErrorBoundaryPage';
import RouterLayoutWrapper from './Components/RouterLayoutWrapper';
import DynamicSection from './Components/DynamicSection';
import useHasPermission from '../Hooks/useHasPermission';

export default function useRouter(userData = null, authChecked = false) {
  // The two pages registered here by name are gated on what they read, matching the cog in the
  // navbar and the credits panel that link to them. Everything else is resolved from the server
  // route tree, which is already filtered per user.
  const canViewSettings = useHasPermission('settings.view');
  const canViewPurchases = useHasPermission('purchases.viewhistory');
  const logged = Boolean(userData);
  // On reload, getUserInfo runs in an effect, so the first render always looks
  // logged out. Nothing may redirect during that window or the requested URL is
  // discarded before it can be matched — hold the location and render nothing;
  // the global loading backdrop is already showing. (The equivalent window for
  // the route tree is handled inside DynamicSection.)
  const authPending = !authChecked && !logged;

  // RouterProvider does not adopt a replaced `router` prop — it keeps rendering
  // the router it first mounted with, so this key remounts it when the config
  // genuinely changes. The config no longer depends on the fetched route tree
  // (DynamicSection resolves pages at render time), so this changes only on
  // login/logout and role changes, not every time routes load.
  const routerKey = useMemo(
    () => [logged, authChecked, canViewSettings, canViewPurchases].join('|'),
    [logged, authChecked, canViewSettings, canViewPurchases]
  );

  // Memoized: createBrowserRouter builds a new router with its own internal
  // state, so calling it on every render would throw that state away.
  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          element: <RouterLayoutWrapper />,
          errorElement: <ErrorBoundaryPage />,
          children: [
            logged
              ? {
                  path: '/',
                  element: <AuthenticatedLayoutPage />,
                  children: [
                    ...(canViewSettings
                      ? [{ path: 'settings', element: <SettingsPage /> }]
                      : []),
                    ...(canViewPurchases
                      ? [
                          {
                            path: 'purchaseHistory',
                            element: <PurchaseHistoryPage />,
                          },
                        ]
                      : []),
                    {
                      path: 'changePassword',
                      element: <ChangePwd />,
                    },
                    {
                      path: 'setup2FA',
                      element: <Setup2FA />,
                    },
                    {
                      path: 'setupFIDO2',
                      element: <SetupFIDO2 />,
                    },
                    {
                      path: '401',
                      element: <NotAuthenticatedPage noRedirect />,
                    },
                    // Every feature page is served from here, resolved against
                    // the server tree at render time.
                    {
                      path: '*',
                      element: <DynamicSection />,
                    },
                  ],
                }
              : {
                  element: <UnauthenticatedLayoutPage />,
                  children: [
                    {
                      path: '/',
                      element: authPending ? <></> : <LoginPage />,
                    },
                    {
                      path: 'verify2FA',
                      element: <Verify2FAPage />,
                    },
                    {
                      path: '401',
                      element: <NotAuthenticatedPage />,
                    },
                    {
                      path: 'landingPage/:landingId/:activityId',
                      element: <LandingPage />,
                    },
                    {
                      path: 'callToAction/:ctaId/:activityId',
                      element: <CallToActionPage />,
                    },
                    {
                      path: 'responseRegistered',
                      element: <ResponseRegisteredPage />,
                    },
                    {
                      path: '*',
                      element: authPending ? (
                        <></>
                      ) : (
                        <Navigate to="/" replace />
                      ),
                    },
                  ],
                },
          ],
        },
      ]),
    [logged, canViewSettings, canViewPurchases, authPending]
  );

  return { router, routerKey };
}
